import _ from "lodash";
import {
  updateEventModalV2,
  setDailyRepeatEventSettings,
  setMonthlyDayPositionsRepeatEventSettings,
  setMonthlySetpositionsRepeatEventSettings,
  setWeeklyRepeatEventSettings,
} from "MyCalendar/modules/MyCalendarModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import ACLStore from "lib/aclStore";
import {
  STAFF,
  STUDENT,
  STUDENT_USER,
  STAFF_USER,
  FAMILY_MEMBER,
  PARENT,
  HOLIDAYS_AND_VACATIONS,
} from "Constants/stringConstants";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import {
  getUserEventCreationAccessLevel,
  convertTimeToMinutes,
} from "MyCalendar/modules/Utils";
import { RRule } from "rrule";
import { getDateMomentObject, formatDate } from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

export const Z_INDEX = 1100;

export const GROUP_BY_OPTIONS = {
  INDIVIDUAL: "INDIVIDUAL",
  COURSE: "COURSE",
  GRADE: "GRADE",
  YEAR_GROUP: "YEAR_GROUP",
};

export const SHARE_WITH_RECIPIENT_TYPE = {
  STAFF: STAFF_USER,
  STUDENT: STUDENT_USER,
  FAMILY_MEMBER,
};

export const mapSharedWithToUserType = {
  [SHARE_WITH_RECIPIENT_TYPE.STUDENT]: STUDENT,
  [SHARE_WITH_RECIPIENT_TYPE.STAFF]: STAFF,
  [SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER]: PARENT,
};

export const ENTITY_TYPE = {
  GRADES: "GRADES",
  CLASSES: "CLASSES",
  CUSTOM: "CUSTOM",
  STAFF_MEMBERS: "STAFF_MEMBERS",
  STUDENTS: "STUDENTS",
  PARENTS: "PARENTS",
};

export const TYPENAME_MAPPING = {
  Grade: "GRADE",
  Course: "COURSE",
};

export const ENTITY_TYPE_MAPPING = {
  CLASSES: "COURSE",
  GRADES: "GRADE",
  CUSTOM: "USER",
};

export const USER_TYPES = {
  staff: STAFF_USER,
  student: STUDENT_USER,
  parent: FAMILY_MEMBER,
};

export const MAP_ENTITY_TYPE_TO_LABEL = {
  [ENTITY_TYPE.GRADES]: "common:grade_plural",
  [ENTITY_TYPE.CLASSES]: "common:class_plural",
  [ENTITY_TYPE.CUSTOM]: "common:custom",
};

export const setValue = (fieldName, updateEventModalV2) => {
  return function (value) {
    return updateEventModalV2({
      [fieldName]: value,
    });
  };
};

export const mapStateToProps = state => {
  return {
    eventModalV2: state.myCalendar.eventModal,
  };
};

export const mapActionCreators = {
  updateEventModalV2,
  setWeeklyRepeatEventSettings,
  setMonthlySetpositionsRepeatEventSettings,
  setMonthlyDayPositionsRepeatEventSettings,
  setDailyRepeatEventSettings,
  setToastMsg,
};

export const getPresetReminders = (t, isAllDayEvent = false) => {
  if (isAllDayEvent) {
    // Day-based options for all-day events
    return [
      {
        value: 1 * 24 * 60,
        label: t("calendar:count_day_before", { count: 1 }),
      },
      {
        value: 2 * 24 * 60,
        label: t("calendar:count_day_before", { count: 2 }),
      },
      {
        value: 3 * 24 * 60,
        label: t("calendar:count_day_before", { count: 3 }),
      },
      {
        value: 5 * 24 * 60,
        label: t("calendar:count_day_before", { count: 5 }),
      },
      {
        value: 10 * 24 * 60,
        label: t("calendar:count_day_before", { count: 10 }),
      },
      {
        value: 15 * 24 * 60,
        label: t("calendar:count_day_before", { count: 15 }),
      },
      { value: "custom", label: t("calendar:custom") },
    ];
  }

  // Minute/hour-based options for regular events
  return [
    { value: 5, label: t("calendar:count_min_before", { count: 5 }) },
    { value: 10, label: t("calendar:count_min_before", { count: 10 }) },
    { value: 15, label: t("calendar:count_min_before", { count: 15 }) },
    { value: 30, label: t("calendar:count_min_before", { count: 30 }) },
    { value: 60, label: t("calendar:count_hour_before", { count: 1 }) },
    { value: 24 * 60, label: t("calendar:count_day_before", { count: 1 }) },
    { value: "custom", label: t("calendar:custom") },
  ];
};

/**
 * Groups reminders by their schedule time and merges their sentVia channels into unique arrays.
 * Transforms the grouped data into an array of event reminders with reminderOffset and channels.
 *
 * @param {Array<Object>} reminders - Array of reminder objects
 * @param {string|number} reminders[].scheduleTime - The scheduled time for the reminder
 * @param {Array<string>} [reminders[].sentVia=[]] - Array of channels the reminder is sent via
 * @returns {Array<Object>} Array of event reminder objects
 * @returns {number} returns[].reminderOffset - The reminder time offset as a number
 * @returns {Array<string>} returns[].channels - Unique array of channels for this reminder time
 */
const groupRemindersByValueAndMergeSentVia = reminders => {
  const eventReminders = [];

  // Iterate through each reminder and group them by scheduleTime
  // and merge their sentVia channels into unique arrays
  const groupRemindersByTime = _.reduce(
    reminders,
    (remindersMapping, { scheduleTime, sentVia = [] }) => {
      remindersMapping[scheduleTime] = _.uniq([
        ...(remindersMapping[scheduleTime] || []),
        ...sentVia,
      ]);
      return remindersMapping;
    },
    {}
  );

  // Transform the grouped data into an array of event reminders
  _.forEach(groupRemindersByTime, (sentVia, scheduleTime) => {
    if (!_.isEmpty(sentVia) && !_.isEmpty(scheduleTime)) {
      eventReminders.push({
        reminderOffset: Number(scheduleTime),
        channels: sentVia,
      });
    }
  });
  return eventReminders;
};

const getRepeatEventSettings = repeatEventSettings => {
  if (!repeatEventSettings) return null;
  const {
    frequency,
    interval,
    occurrences,
    weekDays,
    monthDays,
    setPositions,
    count,
    endDate,
    timezone,
  } = repeatEventSettings;
  const baseSetting = {
    frequency,
    interval,
    occurrences,
    weekDays,
    monthDays,
    setPositions,
    timezone,
  };
  if (count) {
    baseSetting.count = count;
    baseSetting.endDate = null;
  }
  if (endDate && getDateMomentObject(endDate).isValid()) {
    baseSetting.endDate = formatDate({
      date: getDateMomentObject(endDate),
      format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
    });
  }
  if (frequency === "DAILY") {
    return {
      ...baseSetting,
      monthDays: [],
      weekDays: [],
      setPositions: [],
    };
  }
  if (frequency === "WEEKLY") {
    return {
      ...baseSetting,
      monthDays: [],
      setPositions: [],
    };
  }
  if (frequency === "MONTHLY") {
    return {
      ...baseSetting,
    };
  }
};

const RECURRING_TYPE_MAPPING = {
  school_day: "EVERY_SCHOOL_DAY",
  weekly: "EVERY_WEEK",
  every_2_weeks: "EVERY_TWO_WEEKS",
  monthly_by_date: "MONTHLY_BY_DATE",
  monthly_by_day: "MONTHLY_BY_DAY",
  custom: "CUSTOM",
};

/**
 * Groups reminders by their schedule time and merges their sentVia channels into unique arrays.
 * Transforms the grouped data into an array of event reminders with reminderOffset and channels.
 *
 * @param {Array<Object>} reminders - Array of reminder objects
 * @param {string|number} reminders[].scheduleTime - The scheduled time for the reminder
 * @param {Array<string>} [reminders[].sentVia=[]] - Array of channels the reminder is sent via
 * @returns {Array<Object>} Array of event reminder objects
 * @returns {number} returns[].reminderOffset - The reminder time offset as a number
 * @returns {Array<string>} returns[].channels - Unique array of channels for this reminder time
 */
const groupRemindersByValueAndMergeSentViaAndTimeOffsets = reminders => {
  const eventReminders = [];

  // Iterate through each reminder and group them by scheduleTime
  // and merge their sentVia channels into unique arrays
  const groupRemindersByTime = _.reduce(
    reminders,
    (
      remindersMapping,
      { scheduleTime, sentVia = [], triggerDayOffset, triggerTimeOffset }
    ) => {
      const dayTimeInMinutes = convertTimeToMinutes(triggerTimeOffset);
      // 1 day before 9 AM means 1 day - 9 hours = 15 hours = 900 minutes before the event start time
      // 2 days before 9 AM means 2 days - 9 hours = 39 hours = 2340 minutes before the event start time
      // So, we need to subtract the triggerTimeOffset (in minutes) from the scheduleTime (in minutes)
      const finalScheduledTime = scheduleTime - dayTimeInMinutes;
      remindersMapping[finalScheduledTime] = {
        sentVia: _.uniq([
          ...(remindersMapping[finalScheduledTime] || []),
          ...sentVia,
        ]),
        triggerDayOffset,
        triggerTimeOffset,
      };
      return remindersMapping;
    },
    {}
  );

  // Transform the grouped data into an array of event reminders
  _.forEach(
    groupRemindersByTime,
    ({ sentVia, triggerDayOffset, triggerTimeOffset }, scheduleTime) => {
      if (!_.isEmpty(sentVia) && !_.isEmpty(scheduleTime)) {
        eventReminders.push({
          reminderOffset: Number(scheduleTime),
          channels: sentVia,
          triggerDayOffset: `${triggerDayOffset}`,
          triggerTimeOffset,
        });
      }
    }
  );
  return eventReminders;
};

export const getEventDetails = (eventModalV2, message = "") => {
  const {
    attachments,
    description,
    endDate,
    eventId,
    eventTitle,
    eventType,
    isAllDayEvent,
    location,
    participants,
    reminders,
    startDate,
    initialAttachments,
    sharedWith,
    entityType,
    shouldNotifyParticipants,
    repeatEventSettings,
    skipNonOperationalDays,
    repeatScope,
    isRepeatEvent,
  } = eventModalV2;

  const eventReminders = isAllDayEvent
    ? groupRemindersByValueAndMergeSentViaAndTimeOffsets(reminders)
    : groupRemindersByValueAndMergeSentVia(reminders);
  const filteredSharedWith =
    entityType === "CUSTOM"
      ? _.filter(sharedWith, userType => {
          return _.some(participants[entityType], participant => {
            return _.isEqual(USER_TYPES[_.get(participant, "type")], userType);
          });
        })
      : sharedWith;
  const baseDetails = {
    start: startDate,
    end: endDate,
    entityIds: _.map(participants[entityType], "id"),
    categoryId: eventType,
    title: _.trim(eventTitle),
    description: _.trim(description),
    location: _.trim(location),
    isFullDayEvent: isAllDayEvent,
    attachments,
    sharedWith: _.map(filteredSharedWith, userType =>
      _.isEqual(userType, "FAMILY_MEMBER") ? "PARENT" : userType
    ),
    entityType: ENTITY_TYPE_MAPPING[entityType],
    eventReminders,
    shouldNotifyParticipants,
    recurrenceSetting: isRepeatEvent
      ? {
          ...getRepeatEventSettings(repeatEventSettings),
        }
      : null,
    metadata: {
      skipNonOperationalDays: skipNonOperationalDays,
      recurrenceType: RECURRING_TYPE_MAPPING[repeatEventSettings?.id],
    },
  };

  return eventId
    ? {
        ...baseDetails,
        id: eventId,
        initialAttachments,
        message,
        actionFor: repeatScope || "ALL",
      }
    : {
        ...baseDetails,
        action: "CREATE",
      };
};

export const getVideoMeetingDetails = (eventModalV2, message = "") => {
  const {
    start,
    end,
    entityIds,
    title,
    description,
    location,
    isFullDayEvent,
    attachments,
    sharedWith,
    entityType,
    eventReminders,
    shouldNotifyParticipants,
    recurrenceSetting,
    metadata,
    id: videoMeetingId,
    initialAttachments,
    actionFor,
  } = getEventDetails(eventModalV2, message);
  const { meetingService, meetingLink, thirdPartyMeetingLinkData } =
    eventModalV2 || {};
  const meeting =
    meetingService === "OTHER"
      ? {
          url: meetingLink,
          type: "OTHER",
        }
      : {
          id: thirdPartyMeetingLinkData?.id,
        };
  const videoMeetingBaseDetails = {
    start,
    end,
    entityIds,
    title,
    description,
    location,
    isFullDayEvent,
    attachments,
    sharedWith,
    entityType,
    eventReminders,
    shouldNotifyParticipants,
    recurrenceSetting,
    metadata,
    meeting,
  };
  return videoMeetingId
    ? {
        ...videoMeetingBaseDetails,
        id: videoMeetingId,
        initialAttachments,
        message,
        actionFor,
      }
    : {
        ...videoMeetingBaseDetails,
        action: "CREATE",
      };
};

export const isInvalidEventForm = eventModalV2 => {
  const {
    eventTitle,
    eventType,
    startDate,
    endDate,
    totalParticipantUserCount,
  } = eventModalV2 || {};

  return (
    _.isEmpty(_.trim(eventTitle)) ||
    _.isEmpty(eventType) ||
    _.isEmpty(startDate) ||
    _.isEmpty(endDate) ||
    totalParticipantUserCount <= 0
  );
};

export const isInvalidVideoMeetingForm = eventModalV2 => {
  const {
    eventTitle,
    startDate,
    endDate,
    totalParticipantUserCount,
    meetingLink,
    meetingService,
    thirdPartyMeetingLinkData,
  } = eventModalV2 || {};

  return (
    _.isEmpty(_.trim(eventTitle)) ||
    _.isEmpty(startDate) ||
    _.isEmpty(endDate) ||
    _.isEmpty(meetingService) ||
    (_.isEmpty(meetingLink) && _.isEmpty(thirdPartyMeetingLinkData)) ||
    totalParticipantUserCount <= 0
  );
};

export const getIsHolidayEventType = eventType => {
  return _.isEqual(eventType, HOLIDAYS_AND_VACATIONS);
};

export const getFilteredEventCreationPopupListOptions = (
  options,
  haveOnlyWholeDayEventAndHolidayTag
) => {
  const isAdmin = getIsAdminByRole();
  if (isAdmin) return options;

  return _.filter(options, option => {
    if (_.get(option, "key") === MY_CALENDAR_CONSTANTS.CREATE_EVENT) {
      return !haveOnlyWholeDayEventAndHolidayTag;
    }
    return true;
  });
};

// Convert minutes to human readable format
export const formatScheduleTime = (minutes, t) => {
  if (!minutes || minutes <= 0) return minutes;

  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const remainingMinutes = minutes % 60;

  const parts = [];

  if (days > 0) {
    parts.push(t("calendar:count_days", { count: days }));
  }

  if (hours > 0) {
    parts.push(t("calendar:count_hours", { count: hours }));
  }

  if (remainingMinutes > 0) {
    parts.push(t("calendar:count_minutes", { count: remainingMinutes }));
  }

  return {
    value: minutes,
    label: `${parts.join(" ")} ${_.toLower(t("common:before"))}`,
  };
};

export const getRemindersWithInitial = (
  initialReminder,
  t,
  isAllDayEvent = false
) => {
  const presetReminders = getPresetReminders(t, isAllDayEvent);

  if (!initialReminder) {
    return presetReminders;
  }

  const hasInitialReminderPresentInPresets = _.find(
    presetReminders,
    currPresetReminder =>
      _.get(currPresetReminder, "value") === initialReminder.value
  );

  if (hasInitialReminderPresentInPresets) {
    return presetReminders;
  }

  // Insert initialReminder at the beginning of the list
  // and keep the rest of the preset reminders intact
  // this is to ensure custom reminder always appears at the top of the list to display in the dropdown
  const remindersWithInitial = [...presetReminders, initialReminder];

  return remindersWithInitial;
};

export const getScheduleTimeObject = (
  scheduleTime,
  t,
  isAllDayEvent = false
) => {
  const presetReminders = getPresetReminders(t, isAllDayEvent);
  const reminderObj = _.find(
    presetReminders,
    currPresetReminder => _.get(currPresetReminder, "value") === scheduleTime
  );

  if (reminderObj) {
    return reminderObj;
  }

  return formatScheduleTime(scheduleTime, t);
};
export const getGradeList = ({ gradeList, curriculumPrograms }) => {
  // used map to maintain the order of curriculumPrograms
  const map = _.reduce(
    curriculumPrograms,
    (result, program) => {
      const { id, acronym } = program;
      result.set(id, { label: acronym, groupId: id, options: [] });
      return result;
    },
    new Map()
  );

  _.forEach(gradeList, grade => {
    const { curriculumProgram } = grade;
    const { id, acronym } = curriculumProgram;
    const groupObj = map.get(id);

    if (_.isArray(_.get(groupObj, "options"))) {
      groupObj.options.push({ ...grade, groupId: id });
    } else {
      // ideally it will never go to else block, but just in case
      map.set(id, {
        label: acronym,
        groupId: id,
        options: [{ ...grade, groupId: id }],
      });
    }
  });

  return _.filter([...map.values()], groupObj => !_.isEmpty(groupObj.options));
};

export const groupByOptionsForCustomRecipient = ({
  t,
  currentCurriculumProgramId,
}) => {
  let options = [
    {
      key: GROUP_BY_OPTIONS.INDIVIDUAL,
      value: GROUP_BY_OPTIONS.INDIVIDUAL,
      label: t("announcement:individual_other"),
    },
    {
      key: GROUP_BY_OPTIONS.COURSE,
      value: GROUP_BY_OPTIONS.COURSE,
      label: t("common:class_other"),
    },
    {
      key: GROUP_BY_OPTIONS.GRADE,
      value: GROUP_BY_OPTIONS.GRADE,
      label: t("common:grades"),
    },
    // {
    //   key: GROUP_BY_OPTIONS.YEAR_GROUP,
    //   value: GROUP_BY_OPTIONS.YEAR_GROUP,
    //   label: _.startCase(t("common:year_group_plural")),
    // },
  ];
  const userEventCreationAccessLevel = getUserEventCreationAccessLevel({
    currentCurriculumProgramId,
    alsoCheckClassLevelPermission: false,
  });

  const userCanCreateHolidaysAndWholeSchoolEvents = _.includes(
    ["ACCOUNT_LEVEL", "PROGRAM_LEVEL"],
    userEventCreationAccessLevel
  );
  options = !userCanCreateHolidaysAndWholeSchoolEvents
    ? _.filter(
        options,
        option => _.get(option, "key") !== GROUP_BY_OPTIONS.GRADE
      )
    : options;
  return options;
};

export const getClassList = ({ filteredClasses, programFilter }) => {
  const obj = {};

  _.forEach(filteredClasses, classItem => {
    const { grades, curriculumProgram } = classItem;

    if (programFilter) {
      const { acronym, id } = curriculumProgram;
      if (_.isArray(obj[id]?.options)) {
        obj[id].options.push(classItem);
      } else {
        obj[id] = { label: acronym, groupId: id, options: [classItem] };
      }
    } else {
      _.map(grades, item => {
        const { name, id, displaySequence } = item;

        if (_.isArray(obj[id]?.options)) {
          obj[id].options.push({ ...classItem, groupId: id });
        } else {
          obj[id] = {
            label: name,
            groupId: id,
            displaySequence,
            options: [{ ...classItem, groupId: id }],
          };
        }
      });
    }
  });
  return _.sortBy(_.values(obj), "displaySequence");
};

export const formatClassesStructure = classEdges => {
  return _.map(classEdges, ({ node }) => ({
    id: node?.id,
    title: node?.title,
    grades: _.map(node?.grades, grade => ({
      id: grade?.id,
      name: grade?.name,
      displaySequence: grade?.displaySequence,
    })),
    staffCount: _.get(node, "collaborator.totalCount", 0),
    studentCount: _.get(node, "students.totalCount", 0),
    familyMemberCount: _.get(node, "familyMembers.totalCount", 0),
  }));
};

export const formatClassesStructureAndFilterNoGrades = classEdges => {
  // Filtering out classes that do not have grades
  return _.filter(
    formatClassesStructure(classEdges),
    ({ grades }) => !_.isEmpty(grades)
  );
};

export const getCustomScheduleOptions = t => {
  return [
    {
      value: "minutes",
      label: t("calendar:minutes"),
    },
    {
      value: "hours",
      label: t("calendar:hours"),
    },
    {
      value: "days",
      label: t("calendar:days"),
    },
    {
      value: "weeks",
      label: t("calendar:weeks"),
    },
  ];
};

export const getCustomScheduleOptionsForAllDay = t => {
  return [
    {
      value: "days",
      label: t("calendar:days"),
    },
    {
      value: "weeks",
      label: t("calendar:weeks"),
    },
  ];
};

export const addTimeToDate = (date, dateWithTime) => {
  const _dateWithTime = dateWithTime || getDateMomentObject();
  return date?.clone()?.set({
    hour: _dateWithTime?.hour(),
    minute: _dateWithTime?.minute(),
    second: _dateWithTime?.second(),
  });
};

export const MAX_REMINDER_COUNT = 3;

export const getIsHolidayEvent = eventType => {
  return _.isEqual(eventType, HOLIDAYS_AND_VACATIONS);
};

export const MAP_LIST_TYPE_TO_QUERY_ENTITY = {
  [ENTITY_TYPE.GRADES]: "GRADE",
  [ENTITY_TYPE.CLASSES]: "COURSE",
  [ENTITY_TYPE.CUSTOM]: "CUSTOM",
};

export const isEmptyForm = eventModalV2 => {
  const {
    eventTitle,
    eventType,
    location,
    description,
    attachments,
    reminders,
  } = eventModalV2 || {};
  const isEmptyForm =
    _.isEmpty(eventTitle) &&
    _.isEmpty(eventType) &&
    _.isEmpty(location) &&
    _.isEmpty(description) &&
    _.isEmpty(attachments) &&
    _.size(reminders) === 1;
  return isEmptyForm;
};

/**
 * Determines if the current user can publish events to all recipients
 * @param {string} currentCurriculumProgramId - The current curriculum program ID
 * @returns {boolean} - True if user can publish to all recipients
 */
export const canUserCreateEventForEveryone = ({
  currentCurriculumProgramId,
}) => {
  const isUserAdminByRole = getIsAdminByRole();
  const rnpEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
  const userEventCreationAccessLevel = getUserEventCreationAccessLevel({
    currentCurriculumProgramId,
    alsoCheckClassLevelPermission: false,
  });

  return rnpEnabled
    ? _.isEqual(userEventCreationAccessLevel, "ACCOUNT_LEVEL")
    : isUserAdminByRole;
};

export const DEFAULT_OCCURRENCES = 2;
export const EVENT_MODES = {
  CREATE: "create",
  EDIT: "edit",
  DUPLICATE: "duplicate",
};

export const EVENT_MODE_LABELS = {
  [EVENT_MODES.CREATE]: "calendar:create_event",
  [EVENT_MODES.EDIT]: "calendar:edit_event",
  [EVENT_MODES.DUPLICATE]: "calendar:duplicate_event",
};
export const EVENT_MODE_LABELS_VIDEO_MEETING = {
  [EVENT_MODES.CREATE]: "calendar:create_video_meeting",
  [EVENT_MODES.EDIT]: "calendar:edit_video_meeting",
};

/**
 * Calculates the number of days that can be added to the current date to reach the academic year end date
 * @param {moment.Moment|string} academicYearEndDate - The academic year end date
 * @param {moment.Moment|string} eventStartDate - The current date
 * @returns {number} - Number of days between current date and academic year end date (0 if end date is past)
 */
export const getDaysUntilAcademicYearEnd = (
  academicYearEndDate,
  eventStartDate
) => {
  const endDate = getDateMomentObject(academicYearEndDate);
  const currentMoment = getDateMomentObject(eventStartDate);

  const daysDifference = endDate.diff(currentMoment, "days");

  // Return 0 if academic year has already ended
  return Math.max(0, daysDifference);
};

const rrFrequency = {
  DAILY: RRule.DAILY,
  WEEKLY: RRule.WEEKLY,
  MONTHLY: RRule.MONTHLY,
};

/**
 * Calculates the final occurrence count based on frequency and total days
 * @param {string} frequency - The frequency type ('DAILY', 'WEEKLY', 'MONTHLY')
 * @param {number} totalDays - The total number of days
 * @returns {number} - The calculated occurrence count based on frequency
 */
export const calculateOccurrencesByFrequency = (
  repeatEventSettings,
  academicEndDate,
  eventStartDate
) => {
  const { frequency, interval, endDate, setPositions, monthDays, weekDays } =
    repeatEventSettings || {};
  if (!frequency) {
    return 0;
  }
  // Create a rule:
  const rrule = {
    freq: rrFrequency[frequency],
    interval: interval,
    byweekday: weekDays,
    bymonthday: monthDays,
    bysetpos: setPositions,
    until: endDate ? getDateMomentObject(endDate).toDate() : null,
    dtstart: getDateMomentObject(eventStartDate).toDate(),
  };
  const rule = new RRule(
    !getDateMomentObject(endDate).isValid() ? _.omit(rrule, "until") : rrule
  );

  return rule.between(
    getDateMomentObject(eventStartDate).toDate(),
    getDateMomentObject(academicEndDate).toDate()
  ).length;
};

export const getRRulefromRepeatEventSettings = (
  repeatEventSettings,
  startDate
) => {
  const {
    frequency,
    interval,
    endDate,
    setPositions,
    monthDays,
    weekDays,
    count,
  } = repeatEventSettings || {};
  try {
    const rrule = {
      freq: rrFrequency[frequency],
      interval: interval,
      byweekday: weekDays,
      bymonthday: monthDays,
      bysetpos: setPositions,
      count,
      until: endDate
        ? getDateMomentObject(endDate).startOf("day").toDate()
        : null,
      dtstart: getDateMomentObject(startDate).startOf("day").toDate(),
    };
    const rule = new RRule(
      !getDateMomentObject(endDate).isValid() ? _.omit(rrule, "until") : rrule
    );
    return rule;
  } catch {
    return { toString: () => "" };
  }
};

export const getIsRecurringEventSettingsUpdated = (
  readOnlyExistingRepeatEventSettings,
  repeatEventSettings,
  startDate
) => {
  if (!startDate) return false;
  const existingRRule = getRRulefromRepeatEventSettings(
    readOnlyExistingRepeatEventSettings,
    startDate
  );
  const newRRule = getRRulefromRepeatEventSettings(
    repeatEventSettings,
    startDate
  );
  return !_.isEqual(existingRRule.toString(), newRRule.toString());
};

export const getWeekOfMonth = weekOfMonth => {
  return weekOfMonth === 5 ? -1 : weekOfMonth; // -1 indicates last occurrence of the weekday in the month
};

/**
 * Returns repeat event settings with formatted endDate or null based on whether event is recurring
 * @param {Object} params - The function parameters
 * @param {Object} params.repeatEventSettings - The repeat event settings object
 * @param {string} params.repeatEventSettings.endDate - The end date for the recurring event
 * @param {Array} [params.repeatEventSettings.setPositions] - The set positions for monthly recurrence
 * @param {string} params.repeatEventSettings.timezone - The timezone (will be omitted from return)
 * @param {boolean} params.isRepeatEvent - Whether the event is a recurring event
 * @returns {Object|null} - Formatted repeat settings object without timezone, or null if not a repeat event
 */
export const getRepeatSettingsOrNull = ({
  repeatEventSettings,
  isRepeatEvent,
}) => {
  const omitKeys = ["timezone", "endType", "id", "repeatType"];
  if (repeatEventSettings?.count) {
    omitKeys.push("endDate");
  }
  return isRepeatEvent
    ? _.omit(
        {
          count: null,
          ...repeatEventSettings,
          endDate: getDateMomentObject(repeatEventSettings?.endDate).format(
            DATE_TIME_CONSTANTS.YYYY_MM_DD
          ),
          setPositions: repeatEventSettings?.setPositions || [],
        },
        omitKeys
      )
    : null;
};

export const isNewVideoMeetingUIFeatureEnabled = () => {
  return ACLStore.can("FeatureFlag:VideoMeetingRevamp");
};

export const isVideoMeetingAdminEditEnabled = () => {
  return ACLStore.can("Common:VideoMeetingAdminEdit");
};

export const isMeetingToastNotificationEnabled = () => {
  return ACLStore.can("FeatureFlag:MeetingToastNotification");
};
